import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Heart, MessageCircle, Share2, Sparkles, Trash2, Clock, UserPlus, UserCheck, ChevronLeft, ChevronRight, X, Calendar, Plus } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import AuthImage from "@/components/AuthImage";
import Avatar from "@/components/Avatar";
import EventCard from "@/components/EventCard";
import { useAuth } from "@/contexts/AuthContext";
import useBackdropDismiss from "@/lib/useBackdropDismiss";

function timeLeft(expiresAt) {
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return "expired";
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export default function Feed() {
  const { user } = useAuth();
  const [stories, setStories] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [events, setEvents] = useState([]);
  const [active, setActive] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [friendStatus, setFriendStatus] = useState("none");
  const [searchParams, setSearchParams] = useSearchParams();

  // Sync the deep-link param with the active story so refresh / share works.
  const setStoryParam = useCallback(
    (id) => {
      const next = new URLSearchParams(searchParams);
      if (id) next.set("story", id);
      else next.delete("story");
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const closeStory = useCallback(() => {
    setActive(null);
    setStoryParam(null);
  }, [setStoryParam]);

  const backdropProps = useBackdropDismiss(closeStory);

  const load = useCallback(async () => {
    try {
      const [s, b, e] = await Promise.all([
        api.get("/stories"),
        api.get("/broadcasts"),
        api.get("/events"),
      ]);
      setStories(s.data);
      setBroadcasts(b.data);
      setEvents(e.data);
    } catch (e2) {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Deep-link: if the URL carries ?story=<id>, open that story modal once
  // stories are loaded. Useful when navigating from a notification.
  useEffect(() => {
    const sid = searchParams.get("story");
    if (!sid || active?.id === sid || stories.length === 0) return;
    const found = stories.find((s) => s.id === sid);
    if (found) openStory(found);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stories, searchParams]);

  const openStory = async (s) => {
    setActive(s);
    setStoryParam(s.id);
    setCommentText("");
    try {
      const r = await api.get(`/stories/${s.id}/comments`);
      setComments(r.data);
    } catch (e) {
      setComments([]);
    }
    // Load friend status with the author
    if (s.author?.id && s.author.id !== user?.id) {
      try {
        const fs = await api.get(`/friends/status/${s.author.id}`);
        setFriendStatus(fs.data.status);
      } catch (e) {
        setFriendStatus("none");
      }
    } else {
      setFriendStatus("self");
    }
  };

  // Step ±1 through the current visible stories list.
  const stepStory = (delta) => {
    if (!active || stories.length === 0) return;
    const idx = stories.findIndex((s) => s.id === active.id);
    if (idx === -1) return;
    const nextIdx = (idx + delta + stories.length) % stories.length;
    const next = stories[nextIdx];
    if (!next) return;
    setStoryParam(next.id);
    openStory(next);
  };

  // Keyboard: ← → for navigation, Esc to close.
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") stepStory(-1);
      else if (e.key === "ArrowRight") stepStory(1);
      else if (e.key === "Escape") closeStory();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, stories]);

  // Touch swipe on the image pane: ≥ 50px horizontal flick = navigate.
  const touchRef = useRef({ x: 0, y: 0, active: false });
  const onTouchStart = (e) => {
    const t = e.touches?.[0];
    if (!t) return;
    touchRef.current = { x: t.clientX, y: t.clientY, active: true };
  };
  const onTouchEnd = (e) => {
    const start = touchRef.current;
    if (!start.active) return;
    touchRef.current = { x: 0, y: 0, active: false };
    const t = e.changedTouches?.[0];
    if (!t) return;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy)) return;
    stepStory(dx < 0 ? 1 : -1);
  };

  const addFriendOfAuthor = async () => {
    if (!active?.author?.id) return;
    try {
      await api.post(`/friends/request/${active.author.id}`);
      setFriendStatus("pending_outgoing");
    } catch (e) {
      alert(formatApiError(e));
    }
  };

  const toggleLike = async (s) => {
    try {
      const r = await api.post(`/stories/${s.id}/like`);
      setStories((arr) => arr.map((x) => (x.id === s.id ? { ...x, liked: r.data.liked, likes: r.data.likes } : x)));
      if (active && active.id === s.id) setActive({ ...active, liked: r.data.liked, likes: r.data.likes });
    } catch (e) {
      /* ignore */
    }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim() || !active) return;
    try {
      const r = await api.post(`/stories/${active.id}/comment`, { text: commentText });
      setComments((c) => [...c, r.data]);
      setCommentText("");
      setStories((arr) => arr.map((x) => (x.id === active.id ? { ...x, comments: x.comments + 1 } : x)));
    } catch (e) {
      /* ignore */
    }
  };

  const shareStory = async (s) => {
    const url = `${window.location.origin}/feed?story=${s.id}`;
    const text = s.caption || "From Lumière Club";
    if (navigator.share) {
      try {
        await navigator.share({ title: "Lumière Club", text, url });
        return;
      } catch (e) {
        /* fallthrough */
      }
    }
    try {
      await navigator.clipboard.writeText(`${text} ${url}`);
      alert("Link copied — paste to Facebook / 小红书 / Instagram");
    } catch {
      window.prompt("Copy this link:", url);
    }
  };

  const deleteStory = async (s) => {
    if (!window.confirm("Delete this story?")) return;
    try {
      await api.delete(`/stories/${s.id}`);
      setStories((arr) => arr.filter((x) => x.id !== s.id));
      closeStory();
    } catch (e) {
      alert(formatApiError(e));
    }
  };

  return (
    <div className="space-y-10">
      {/* Broadcasts */}
      {broadcasts.length > 0 && (
        <section data-testid="broadcasts-section">
          <h2 className="font-serif text-2xl mb-4 flex items-center gap-2">
            <Sparkles size={18} className="text-[#d4af37]" strokeWidth={1.5} />
            Concierge Offerings
          </h2>
          <div className="space-y-3">
            {broadcasts.map((b) => (
              <BroadcastCard key={b.id} b={b} />
            ))}
          </div>
        </section>
      )}

      {/* Events */}
      <section data-testid="events-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-2xl flex items-center gap-2">
            <Calendar size={18} className="text-[#d4af37]" strokeWidth={1.5} />
            Upcoming Events
          </h2>
          <Link
            to="/events/new"
            className="bg-[#d4af37] text-black px-4 py-2 text-xs uppercase tracking-widest hover:bg-[#f3e5ab] flex items-center gap-2"
            data-testid="event-new-cta"
          >
            <Plus size={14} strokeWidth={2} /> Host
          </Link>
        </div>
        {events.length === 0 ? (
          <div className="border border-dashed border-white/10 rounded-lg p-8 text-center text-white/40 text-sm" data-testid="empty-events">
            No upcoming events. Be the first to host one — pickleball, wine, golf, anything.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="events-grid">
            {events.map((e) => (
              <EventCard key={e.id} event={e} />
            ))}
          </div>
        )}
      </section>

      {/* Stories */}
      <section data-testid="stories-section">
        <h2 className="font-serif text-2xl mb-4 flex items-center gap-2">
          Today's Feed
          <span className="text-xs uppercase tracking-[0.2em] text-white/40 ml-3">
            <Clock size={12} className="inline mr-1" strokeWidth={1.5} /> 24h ephemeral
          </span>
        </h2>

        {stories.length === 0 ? (
          <div className="border border-dashed border-white/10 rounded-lg p-12 text-center text-white/40" data-testid="empty-stories">
            No stories yet. Be the first to share — your post vanishes in 24 hours.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
            {stories.map((s) => (
              <button
                key={s.id}
                onClick={() => openStory(s)}
                className="story-card relative aspect-[3/4] overflow-hidden bg-[#141414] border border-white/10 rounded-md group"
                data-testid={`story-card-${s.id}`}
              >
                <AuthImage path={s.storage_path} className="w-full h-full object-cover" />
                <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/90 to-transparent">
                  <div className="flex items-center gap-2 text-white/90 text-xs">
                    <Avatar path={s.author?.avatar_path} name={s.author?.name} size={20} />
                    <span className="font-medium truncate">{s.author?.name}</span>
                    {s.author?.role === "admin" && (
                      <span className="text-[#d4af37] text-[10px] uppercase tracking-widest">Concierge</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-1 text-[10px] uppercase tracking-widest text-white/60">
                    <span>{timeLeft(s.expires_at)}</span>
                    <span className="flex items-center gap-2">
                      <Heart size={12} strokeWidth={1.5} className={s.liked ? "text-[#d4af37] fill-[#d4af37]" : ""} />
                      {s.likes}
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Modal */}
      {active && (
        <div
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-4 overscroll-contain"
          {...backdropProps}
          data-testid="story-modal"
        >
          {/* Floating close button (always visible, top-right of viewport) */}
          <button
            type="button"
            onClick={closeStory}
            aria-label="Close story"
            className="fixed top-4 right-4 z-[60] w-10 h-10 rounded-full bg-black/60 border border-white/15 text-white/80 hover:text-white hover:border-[#d4af37]/60 backdrop-blur flex items-center justify-center"
            data-testid="story-close"
          >
            <X size={18} strokeWidth={1.5} />
          </button>

          {/* Prev / Next arrow buttons (hidden when only one story is visible) */}
          {stories.length > 1 && (
            <>
              <button
                type="button"
                onClick={() => stepStory(-1)}
                aria-label="Previous story"
                className="fixed left-2 sm:left-4 top-1/2 -translate-y-1/2 z-[60] w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-black/60 border border-white/15 text-white/80 hover:text-white hover:border-[#d4af37]/60 backdrop-blur flex items-center justify-center"
                data-testid="story-prev"
              >
                <ChevronLeft size={22} strokeWidth={1.5} />
              </button>
              <button
                type="button"
                onClick={() => stepStory(1)}
                aria-label="Next story"
                className="fixed right-2 sm:right-4 top-1/2 -translate-y-1/2 z-[60] w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-black/60 border border-white/15 text-white/80 hover:text-white hover:border-[#d4af37]/60 backdrop-blur flex items-center justify-center"
                data-testid="story-next"
              >
                <ChevronRight size={22} strokeWidth={1.5} />
              </button>
            </>
          )}

          <div
            className="relative max-w-3xl w-full bg-[#0a0a0a] border border-white/10 rounded-lg overflow-hidden flex flex-col sm:flex-row max-h-[90vh]"
          >
            <div
              className="sm:w-2/3 bg-black flex items-center justify-center max-h-[60vh] sm:max-h-none"
              onTouchStart={onTouchStart}
              onTouchEnd={onTouchEnd}
              data-testid="story-image-pane"
            >
              <AuthImage path={active.storage_path} className="w-full max-h-[60vh] sm:max-h-[90vh] object-contain" />
            </div>
            <div className="sm:w-1/3 flex flex-col">
              <div className="p-5 border-b border-white/10">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <Avatar path={active.author?.avatar_path} name={active.author?.name} size={40} />
                    <div className="min-w-0">
                      <div className="font-medium truncate">{active.author?.name}</div>
                      <div className="text-xs text-white/40 uppercase tracking-widest mt-0.5">
                        <Clock size={10} className="inline mr-1" strokeWidth={1.5} />
                        {timeLeft(active.expires_at)} left
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {friendStatus === "none" && (
                      <button
                        onClick={addFriendOfAuthor}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 border border-[#d4af37]/50 text-[#d4af37] text-[10px] uppercase tracking-widest hover:bg-[#d4af37] hover:text-black transition"
                        data-testid="story-add-friend"
                      >
                        <UserPlus size={12} strokeWidth={1.5} /> Add
                      </button>
                    )}
                    {friendStatus === "pending_outgoing" && (
                      <span className="text-[10px] uppercase tracking-widest text-white/40" data-testid="story-add-pending">Pending</span>
                    )}
                    {friendStatus === "friends" && (
                      <span className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#d4af37]" data-testid="story-add-friends">
                        <UserCheck size={12} strokeWidth={1.5} /> Friends
                      </span>
                    )}
                    {(active.author_id === user?.id || user?.role === "admin") && (
                      <button onClick={() => deleteStory(active)} className="text-white/40 hover:text-red-400 p-1" data-testid="story-delete">
                        <Trash2 size={16} strokeWidth={1.5} />
                      </button>
                    )}
                  </div>
                </div>
                {active.caption && <p className="text-white/80 mt-3 text-sm leading-relaxed">{active.caption}</p>}
                <div className="flex items-center gap-3 mt-4">
                  <button
                    onClick={() => toggleLike(active)}
                    className="flex items-center gap-1.5 text-sm"
                    data-testid="story-like"
                  >
                    <Heart size={18} strokeWidth={1.5} className={active.liked ? "text-[#d4af37] fill-[#d4af37]" : "text-white/70"} />
                    <span>{active.likes}</span>
                  </button>
                  <span className="flex items-center gap-1.5 text-sm text-white/70">
                    <MessageCircle size={18} strokeWidth={1.5} />
                    {comments.length}
                  </span>
                  <button
                    onClick={() => shareStory(active)}
                    className="ml-auto flex items-center gap-1.5 text-sm text-white/70 hover:text-[#d4af37]"
                    data-testid="story-share"
                  >
                    <Share2 size={16} strokeWidth={1.5} />
                    Share
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="story-comments">
                {comments.length === 0 ? (
                  <p className="text-white/40 text-sm">No comments yet.</p>
                ) : (
                  comments.map((c) => (
                    <div key={c.id} className="text-sm">
                      <span className="text-[#d4af37] font-medium">{c.user_name}</span>{" "}
                      <span className="text-white/80">{c.text}</span>
                    </div>
                  ))
                )}
              </div>
              <form onSubmit={submitComment} className="p-3 border-t border-white/10 flex gap-2">
                <input
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Add a comment…"
                  enterKeyHint="send"
                  className="flex-1 bg-transparent border-b border-white/10 focus:border-[#d4af37] outline-none py-2"
                  data-testid="story-comment-input"
                />
                <button
                  type="submit"
                  disabled={!commentText.trim()}
                  className="text-sm uppercase tracking-widest text-[#d4af37] disabled:opacity-30"
                  data-testid="story-comment-submit"
                >
                  Send
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BroadcastCard({ b }) {
  return (
    <div
      className="border border-[#d4af37]/30 bg-gradient-to-br from-[#d4af37]/5 to-transparent p-5 rounded-lg flex gap-4"
      data-testid={`broadcast-${b.id}`}
    >
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#d4af37]">{b.type}</div>
        <h3 className="font-serif text-xl mt-1">{b.title}</h3>
        <p className="text-white/70 text-sm mt-2 leading-relaxed">{b.body}</p>
        {b.cta_url && (
          <a
            href={b.cta_url}
            target="_blank"
            rel="noreferrer"
            className="inline-block mt-3 text-[#d4af37] text-xs uppercase tracking-widest hover:text-[#f3e5ab]"
            data-testid={`broadcast-cta-${b.id}`}
          >
            {b.cta_label || "Learn more"} →
          </a>
        )}
        <div className="text-[10px] uppercase tracking-widest text-white/40 mt-3">
          {b.author_name} · {new Date(b.created_at).toLocaleDateString()}
        </div>
      </div>
      {b.image_path && (
        <div className="w-28 h-28 rounded-md overflow-hidden bg-[#141414] flex-shrink-0">
          <AuthImage path={b.image_path} className="w-full h-full object-cover" />
        </div>
      )}
    </div>
  );
}
